/* eslint-disable jsx-a11y/no-autofocus */
import { forwardRef, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { reCachePromise, useDebounce, useTypewriterText } from '@medifind/hooks';
import { IconSearch } from '@medifind/icons';
import { conditionAutocomplete } from '@medifind/interface';
import { Autocomplete } from '@medifind/shared-basic-components';
import { Filter } from './Filter';
import styles from './FilterShared.module.scss';

const ConditionFilter = forwardRef(
  (
    {
      onConditionChange,
      initialInput,
      prepopulate,
      classes = {},
      what,
      className,
      selectValue,
      onSuggestionsLoaded,
      onSuggestionsSet,
      label = 'Condition',
      showSearchIcon = false,
      actLikeASelect = true,
      onConditionEdit,
      childrenOutsideWrapper,
      childrenPostMenuWrapper,
      fullWidthMenuList,
      onFocus,
      onBlur,
      onMenuStatusChange,
      noHeader,
      smallAt,
      size,
      placeholder = 'Search for a condition or procedure...',
      inputIcon,
      showWrapper = true,
      useTypewriterPlaceholder = false,
      clearInputAfterSelect,
      selectedValues,
      disabled,
      projectIdFilter,
      style,
    },
    ref,
  ) => {
    const [suggestions, setSuggestions] = useState([]);
    const [inputValue, setInputValue] = useState(initialInput || {});
    const { writtenText } = useTypewriterText({ text: placeholder, time: 100, delay: 2000, on: useTypewriterPlaceholder });
    /**
     * CONDITION AUTOCOMPLETE
     */

    useDebounce();
    const autocomplete = (value) => {
      return reCachePromise(
        'conditionFilterAutocomplete',
        async () => {
          if (!value?.toString()?.trim() && !prepopulate) return;
          return conditionAutocomplete({ input: value, showAll: true, projectIdFilter, size });
        },
        [value],
      )
        .then((data) => {
          if (data) {
            if (onSuggestionsLoaded) onSuggestionsLoaded(data);
            if (data.needCharacters) setSuggestions('Type a few more characters');
            else {
              setSuggestions(
                data.results.map(({ display, projectId, codingType }) => ({
                  display: display,
                  value: projectId,
                  codingType,
                })),
              );
            }
          }
        })
        .catch((err) => {
          setSuggestions(
            err?.code === 400
              ? {
                  display: 'Only enter a single condition',
                  ariaType: 'aria-errormessage',
                  menuItem: classNames(styles['autocomplete-error-message']),
                }
              : '',
          );
        });
    };

    const filteredSuggestions = useMemo(() => {
      if (typeof suggestions === 'string' || (typeof suggestions === 'object' && !Array.isArray(suggestions))) return suggestions;
      if (!suggestions) return suggestions;
      return suggestions.map(({ value, ...rest }) => ({
        value,
        ...rest,
        isDisabled: selectedValues?.includes(value),
      }));
    }, [selectedValues, suggestions]);

    useEffect(() => {
      onSuggestionsSet && onSuggestionsSet(filteredSuggestions);
    }, [filteredSuggestions]);

    /**
     * MODE SELECTOR
     */

    const mode = {
      label,
      placeholder: useTypewriterPlaceholder ? writtenText : placeholder,
      autocomplete: autocomplete,
      route: (item) => onConditionChange(item, true),
    };

    useEffect(() => {
      setInputValue(selectValue);
      mode.autocomplete(selectValue?.display ?? '');
    }, [selectValue]);

    if (prepopulate) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        mode.autocomplete('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
    }
    if (ref) ref.current = { setInputValue, setSuggestions };
    return (
      <Filter
        classes={classes}
        label={mode.label}
        what={what}
        noHeader={noHeader}
        showWrapper={showWrapper}
        smallAt={smallAt}
        placeholder={inputValue ? inputValue.display : mode.placeholder}
        style={style}
      >
        <Autocomplete
          id="ac-condition-filter"
          labelId={'-'} // Is replaced by <Filter>
          label={mode.label}
          hideLabel={true}
          items={filteredSuggestions || []}
          inputValue={inputValue ? inputValue.display : ''}
          placeholder={mode.placeholder}
          onMenuStatusChange={onMenuStatusChange}
          onChange={(value) => {
            setInputValue({ display: value });
            mode.autocomplete(value ?? '');
            mode.route(null);
            if (onConditionEdit) onConditionEdit(value);
          }}
          clearInputAfterSelect={clearInputAfterSelect}
          onSelect={(value, ref) => {
            setInputValue(value);
            mode.route(value);
          }}
          onEnter={(value, { highlightedIndex }) => {
            if (filteredSuggestions == null || typeof filteredSuggestions === 'string') {
              event.nativeEvent.preventDownshiftDefault = true;
            }
          }}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          actLikeASelect={actLikeASelect}
          activeFirstOption={true}
          className={classNames(className, classes.searchBar)}
          classes={{
            menuWrapper: classNames(classes.menuWrapper, styles['filter-menu-wrapper']),
            inputWrapper: classNames(classes.inputWrapper, styles['filter-input-wrapper']),
            menu: classNames(classes.menu),
            label: classNames(classes.label),
            autocomplete: classNames(classes.autocomplete),
            menuItem: classNames(styles['text'], classes.menuItem),
            validMenuItem: styles['arrow'],
            menuList: classNames(classes.menuList),
            input: classNames(classes.input),
            icon: classNames(classes.icon),
            inputNoOpen: true,
          }}
          childrenPreInputWrapper={({ isOpen }) =>
            showSearchIcon && (
              <div className={classNames(styles['icon'], classes.icon)}>
                <IconSearch />
              </div>
            )
          }
          childrenSuggestion={(item) => (
            <span
              dangerouslySetInnerHTML={{
                __html: item.highlight ?? item.display,
              }}
            ></span>
          )}
          childrenPostMenuWrapper={childrenPostMenuWrapper}
          childrenOutsideWrapper={childrenOutsideWrapper}
          fullWidthMenuList={fullWidthMenuList}
          inputIcon={inputIcon}
        />
      </Filter>
    );
  },
);

ConditionFilter.displayName = 'ConditionFilter';
ConditionFilter.propTypes = {
  label: PropTypes.string,
  conditions: PropTypes.arrayOf(PropTypes.string),
  selectedValues: PropTypes.arrayOf(PropTypes.any),
  onConditionChange: PropTypes.func.isRequired,
  onSuggestionsLoaded: PropTypes.func,
  onSuggestionsSet: PropTypes.func,
  what: PropTypes.node,
  className: PropTypes.string,
  classes: PropTypes.object,
  selectValue: PropTypes.object,
  prepopulate: PropTypes.bool,
  initialInput: PropTypes.shape({
    display: PropTypes.string.isRequired,
    value: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  noHeader: PropTypes.bool,
  showSearchIcon: PropTypes.bool,
  actLikeASelect: PropTypes.bool,
  onConditionEdit: PropTypes.func,
  childrenPostMenuWrapper: PropTypes.func,
  childrenOutsideWrapper: PropTypes.func,
  fullWidthMenuList: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onMenuStatusChange: PropTypes.func,
  smallAt: PropTypes.string,
  size: PropTypes.number,
  placeholder: PropTypes.string,
  inputIcon: PropTypes.any,
  showWrapper: PropTypes.bool,
  useTypewriterPlaceholder: PropTypes.bool,
  clearInputAfterSelect: PropTypes.bool,
  projectIdFilter: PropTypes.arrayOf(PropTypes.number),
};

export { ConditionFilter as default, ConditionFilter };
